import { useTenants, useUpdateTenant } from '@api/tenants';
import { ActionsFooter, ApprovalDialogButton } from '@components/Forms';
import TenantAddDomains from '@components/Tenants/TenantAddDomains';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import { Box, Grid } from '@mui/material';
import { stringNotAnyValidationScheme } from '@utils/validation';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import { noop } from 'lodash';
import React, { FC, useMemo } from 'react';
import { ITenant } from 'types/tenants';
import { array, object } from 'yup';

interface ITenantEditProps {
	tenant: ITenant;
}

const TenantEdit: FC<ITenantEditProps> = ({ tenant }) => {
	const { invokeToastableAction } = useToast();
	const updateMutation = useUpdateTenant();
	const { data } = useTenants();

	const tenantNames = useMemo(
		() =>
			(data?.tenants || [])
				.map((t: { name: string }) => t.name)
				.filter((n) => n !== tenant.name),
		[data?.tenants, tenant.name]
	);

	const tenantDomains = useMemo(
		() =>
			(data?.tenants || [])
				.reduce(
					(acc: string[], t: { selfProvisioningDomains?: string[] }) => [
						...acc,
						...(t.selfProvisioningDomains || [])
					],
					[]
				)
				.filter((d) => !tenant.selfProvisioningDomains?.includes(d)),
		[data?.tenants, tenant.selfProvisioningDomains]
	);

	const formik = useFormik<ITenant>({
		enableReinitialize: true,
		initialValues: tenant,
		validationSchema: object({
			name: stringNotAnyValidationScheme(
				true,
				tenantNames,
				'tenants.creation.dialog.input.name.unique'
			),
			selfProvisioningDomains: array().of(
				stringNotAnyValidationScheme(
					false,
					tenantDomains,
					'tenants.creation.dialog.input.domain.unique'
				)
			)
		}),
		onSubmit: async (newTenant) => {
			await invokeToastableAction(
				() => updateMutation.mutateAsync(newTenant),
				'tenants.single.update',
				noop
			);
		}
	});

	return (
		<Box display="flex" flexDirection="column" overflow="hidden">
			<Grid
				paddingLeft={3}
				container
				rowSpacing={4}
				flexGrow={1}
				flexDirection="column"
				flexWrap="nowrap"
				sx={{ '> div': { flexBasis: 0 } }}
				paddingTop={0.5}
			>
				<Grid item container xs={12} sm={8} spacing={2}>
					<Grid item xs={12} sm={6}>
						<TextFieldValidation
							id="name"
							required
							formik={formik}
							fullWidth
							label={intl.tr('tenants.single.settings.tab.details.tenantName')}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextFieldValidation
							id="id"
							formik={formik}
							copy
							fullWidth
							disabled
							label={intl.tr('tenants.single.settings.tab.details.tenantId')}
						/>
					</Grid>
				</Grid>
				<Grid item xs={12} sm={8} flex={1}>
					<TenantAddDomains formik={formik} />
				</Grid>
			</Grid>
			<ActionsFooter>
				<LoadingButton
					color="primary"
					variant="contained"
					loading={formik.isSubmitting}
					disabled={!formik.dirty || !formik.isValid}
					onClick={() => formik.handleSubmit()}
					sx={{ marginRight: 1 }}
				>
					{intl.tr('button.save')}
				</LoadingButton>
				<ApprovalDialogButton
					buttonText={intl.tr('button.cancel')}
					onApprove={formik.resetForm}
					disabled={!formik.dirty}
					title={intl.tr('discard.changes.dialog.title')}
					question={intl.tr('discard.changes.dialog.question')}
					approveText={intl.tr('discard.changes.dialog.approve')}
				/>
			</ActionsFooter>
		</Box>
	);
};

export default TenantEdit;
